/**
 * Application root.
 *
 * Runs unchanged on iOS, Android and Web — React Native Web renders the same
 * component tree, so the donor experience is one codebase.
 */

import { useEffect, useState } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { StatusBar, StyleSheet } from 'react-native';

import { colors } from '../theme';
import { useHydrateAuth } from '../core/auth/useAuth';
import { fetchConfig, keys } from '../core/api/hooks';
import { rememberedConfig } from '../core/api/configCache';
import { SplashScreen } from '../features/splash/SplashScreen';
import { useSplashHold } from '../features/splash/useSplashHold';
import { AppProviders, createQueryClient } from './providers/AppProviders';
import { RootNavigator } from './navigation/RootNavigator';

export default function App() {
  // Reads the stored session once, before any screen decides whether to fetch.
  useHydrateAuth();
  const [queryClient] = useState(createQueryClient);
  const configReady = useSeededConfig(queryClient);
  const showSplash = useSplashHold() || !configReady;

  return (
    <GestureHandlerRootView style={styles.root}>
      <StatusBar
        barStyle={showSplash ? 'light-content' : 'dark-content'}
        backgroundColor={showSplash ? colors.bg.brand : colors.bg.surface}
      />
      {showSplash ? (
        <SplashScreen />
      ) : (
        <AppProviders client={queryClient}>
          <RootNavigator />
        </AppProviders>
      )}
    </GestureHandlerRootView>
  );
}

/**
 * While the splash is up: the settings from last time into the cache, so Home
 * opens knowing whether to offer a tiffin, and a fresh copy asked for at once
 * rather than when Home mounts.
 */
function useSeededConfig(queryClient: ReturnType<typeof createQueryClient>): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    void queryClient.prefetchQuery({ queryKey: keys.config, queryFn: fetchConfig });
    void rememberedConfig()
      .then((config) => {
        // Stale from the start, so it is replaced as soon as the server answers.
        if (config && !queryClient.getQueryData(keys.config)) {
          queryClient.setQueryData(keys.config, config, { updatedAt: 0 });
        }
      })
      .finally(() => {
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [queryClient]);
  return ready;
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});
